import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, message, Drawer } from "antd";
import SingleFileUpload from "../../../Components/Transportation/SingleFileUpload";
import { useDispatch, useSelector } from "react-redux";
import { getAllRolesThunk } from "../../../Store/Slices/Common/RBAC/rbacThunks";
import { updatebenefitThunk } from "../../../Store/Slices/Admin/benifits/benifitThunk";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const tenures = ["<1 year", "1-3 years", "3+ years"];
const employmentStatuses = ["Full-time", "Part-time", "Contractor"];
const benefitTypes = [
  "Health Insurance",
  "Dental Insurance",
  "Vision Insurance",
  "Life Insurance",
  "Gym Membership",
  "Paid Time Off (PTO)",
  "Retirement Savings Plan (e.g., 401(k))",
  "Disability Insurance",
  "Flexible Work Arrangements (Remote/Hybrid)",
  "Tuition Reimbursement",
  "Maternity/Paternity Leave",
  "Childcare Assistance",
  "Commuter Benefits",
  "Employee Stock Options",
  "Performance Bonuses",
  "Wellness Programs",
];

const EditBenefitDrawer = ({
  editDrawerVisible,
  setEditDrawerVisible,
  selectedBenefit,
}) => {
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState([]);
  const [photo, setPhoto] = useState("");
  const dispatch = useDispatch();

  const { roles: AllRoles } = useSelector((state) => state.admin.rbac);
  const { loading } = useSelector((state) => state.admin.benefits);
  const departments = AllRoles?.map((dept) => dept.department.toLowerCase());
  const { t } = useTranslation("common");

  useEffect(() => {
    dispatch(getAllRolesThunk());
  }, [dispatch]);

  // Set initial form values when a benefit is selected
  useEffect(() => {
    if (selectedBenefit) {
      form.setFieldsValue({
        ...selectedBenefit,
        department: selectedBenefit.department?.map(d => d.toLowerCase()) || [],
      });
      setDocuments(selectedBenefit.documents || []);
      setPhoto(selectedBenefit.photo || "");
    }
  }, [selectedBenefit, form]);

  // Handle auto-calculation of company contribution
  const handleEmployeeContributionChange = (e) => {
    const employeeValue = parseFloat(e.target.value);
    if (isNaN(employeeValue)) {
      form.setFieldsValue({ companyContribution: "" });
      return;
    }
    if (employeeValue >= 100) {
      message.error(t("Employee Contribution must be less than 100%"));
      form.setFieldsValue({ companyContribution: 0 });
      return;
    }
    const companyValue = Math.max(0, 100 - employeeValue);
    form.setFieldsValue({ companyContribution: companyValue });
  };

  const handleFileChange = (field, fileData, idx = null) => {
    if (field === "photo") {
      setPhoto(fileData?.url || "");
      form.setFieldsValue({ photo: fileData?.url || "" });
    } else if (field === "documents" && idx !== null) {
      const copy = [...documents];
      copy[idx] = {
        ...copy[idx],
        url: fileData.url,
        expiryDate: fileData.expiryDate ?? copy[idx].expiryDate,
        name: copy[idx].name || fileData.name || `Document_${idx + 1}`,
      };
      setDocuments(copy);
      form.setFields([{ name: ["documents", idx], value: copy[idx] }]);
    }
  };

  const handleRemove = (field, idx = null) => {
    if (field === "photo") {
      setPhoto("");
      form.setFieldsValue({ photo: "" });
    } else if (field === "documents" && idx !== null) {
      const copy = [...documents];
      copy.splice(idx, 1);
      setDocuments(copy);
      form.setFieldsValue({ documents: copy });
      message.success(t("Document removed"));
    }
  };

  const handleFinish = (values) => {
        const filteredDocs = documents.filter(
      (doc) => doc && doc.url 
    );
    const finalData = {
      ...values,
      photo,
      documents:filteredDocs,
      _id: selectedBenefit._id,
    };
    dispatch(updatebenefitThunk({ benefitId: selectedBenefit._id, updatedData: finalData }))
      .then(() => {
        message.success(t("Benefit updated successfully!"));
        setEditDrawerVisible(false);
      })
      .catch((error) => {
        message.error(t("Failed to update benefit."));
        console.error(t("Update error:"), error);
      });
  };

  return (
    <Drawer
      title={
        <div className="text-purple-500 px-4 py-2 rounded-md">
          {selectedBenefit ? `${t("Edit")} ${selectedBenefit.benefitName}` : t("Edit Benefit")}
        </div>
      }
      placement="right"
      width="50%"
      onClose={() => setEditDrawerVisible(false)}
      open={editDrawerVisible}
      bodyStyle={{ padding: "24px" }}
    >
      {selectedBenefit && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="[&_.ant-form-item]:mb-6"
        >
          <Form.Item
            label={t("Benefit Name")}
            name="benefitName"
            rules={[{ required: true, message: t("Please enter benefit name") }]}
          >
            <Input placeholder={t("Enter benefit name")} className="py-2 rounded-lg" />
          </Form.Item>

          <Form.Item
            label={t("Benefit Type")}
            name="benefitType"
            rules={[{ required: true, message: t("Select benefit type") }]}
          >
            <Select placeholder={t("Select benefit type")} className="rounded-lg">
              {benefitTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="font-semibold mb-2">{t("Cost Split")}</div>
          <div className="flex gap-4 mb-4">
            <Form.Item
              name="employeeContribution"
              label={t("Employee Contribution (%)")}
              className="flex-1"
              rules={[
                { required: true, message: t("Enter employee contribution") },
                {
                  validator: (_, value) =>
                    value && value >= 100
                      ? Promise.reject(
                        new Error(t("Employee Contribution must be less than 100%"))
                      )
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                type="number"
                min={0}
                max={99}
                placeholder={t("Enter employee contribution")}
                onChange={handleEmployeeContributionChange}
                className="py-2 rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="companyContribution"
              label={t("Company Contribution (%)")}
              className="flex-1"
              rules={[{ required: true }]}
            >
              <Input
                type="number"
                placeholder={t("Auto-calculated")}
                disabled
                className="py-2 rounded-lg bg-gray-100"
              />
            </Form.Item>
          </div>

          <div className="font-semibold mb-2">{t("Eligibility Rules")}</div>

          <Form.Item
            label={t("Department")}
            name="department"
            rules={[{ required: true, message: t("Select department") }]}
          >
            <Select placeholder={t("Select department")} className="rounded-lg" mode="multiple">
              {departments.map((dep) => (
                <Option key={dep} value={dep}>
                  {dep}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("Tenure")}
            name="tenure"
            rules={[{ required: true, message: "Select tenure" }]}
          >
            <Select placeholder={t("Select tenure")} className="rounded-lg">
              {tenures.map((tenure) => (
                <Option key={tenure} value={tenure}>
                  {tenure}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("Employment Status")}
            name="employmentStatus"
            rules={[{ required: true, message: t("Select employment status") }]}
          >
            <Select placeholder={t("Select employment status")} className="rounded-lg">
              {employmentStatuses.map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="mt-6 mb-2 font-semibold">{t("Documents")}</div>
          {documents.map((doc, index) => (
            <div key={index} className="flex items-center gap-3 mb-3">
              <div className="flex-1">
                <SingleFileUpload
                  key={index}
                  name={["documents", index]}
                  label={doc.name || `${t("Document")} ${index + 1}`}
                  type="optional"
                  onChange={(fileData) =>
                    handleFileChange("documents", fileData || {}, index)
                  }
                  onPreview={(file) => console.log("Preview:", file)}
                  onRemove={() => handleRemove("documents", index)}
                  fileUrl={doc.url}
                  fileName={doc.name}
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemove("documents", index)}
                className="px-3 py-2  text-black rounded-md hover:bg-gray-200"
              >
                ✕
              </button>
            </div>
          ))}



          <Button
            type="dashed"
            onClick={() => setDocuments([...documents, {}])}
            className="mb-4"
          >
            + {t("Add Document")}
          </Button>

          <div className="flex justify-end gap-3 mt-4">
            <Button onClick={() => setEditDrawerVisible(false)}>{t("Cancel")}</Button>
            <button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="btn-gradient"
            >
              {loading ? t("Saving...") : t("Save Changes")}
            </button>
          </div>
        </Form>
      )}
    </Drawer>
  );
};

export default EditBenefitDrawer;